/**
 * Hooks pour magasins, prix et stock (API v2).
 */
import { useState, useEffect } from "react";

const API = process.env.REACT_APP_BACKEND_URL || "http://localhost:8000";
const API_V2 = `${API}/api/v2`;

export function useStores(activeOnly = true) {
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  useEffect(() => {
    let cancelled = false;
    fetch(`${API_V2}/stores?active_only=${activeOnly}`)
      .then((res) => (res.ok ? res.json() : Promise.reject(new Error("Stores"))))
      .then((data) => { if (!cancelled) setStores(Array.isArray(data) ? data : []); })
      .catch((e) => { if (!cancelled) setError(e.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [activeOnly]);
  return { stores, loading, error };
}

export function usePricing(productId) {
  const [pricing, setPricing] = useState(null);
  const [loading, setLoading] = useState(!!productId);
  const [error, setError] = useState(null);
  useEffect(() => {
    if (!productId) {
      setPricing(null);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    fetch(`${API_V2}/pricing/${encodeURIComponent(productId)}`)
      .then((res) => (res.ok ? res.json() : Promise.reject(new Error("Pricing"))))
      .then((data) => { if (!cancelled) setPricing(data); })
      .catch((e) => { if (!cancelled) setError(e.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [productId]);
  return { pricing, loading, error };
}

export function useResolvedPriceBulk(productIds, token) {
  const [results, setResults] = useState({});
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (!productIds || productIds.length === 0) { setResults({}); return; }
    let cancelled = false;
    setLoading(true);
    const headers = { "Content-Type": "application/json" };
    if (token) headers.Authorization = `Bearer ${token}`;
    fetch(`${API_V2}/pricing/resolve-bulk`, {
      method: "POST",
      headers,
      body: JSON.stringify({ product_ids: productIds }),
    })
      .then((res) => (res.ok ? res.json() : Promise.reject(new Error("BulkResolve"))))
      .then((data) => { if (!cancelled) setResults(data.results || {}); })
      .catch(() => { if (!cancelled) setResults({}); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [JSON.stringify(productIds), token]); // eslint-disable-line react-hooks/exhaustive-deps
  return { results, loading };
}

export function useResolvedPrice(productId, token) {
  const [resolved, setResolved] = useState(null);
  const [loading, setLoading] = useState(!!productId);
  useEffect(() => {
    if (!productId) { setResolved(null); setLoading(false); return; }
    let cancelled = false;
    setLoading(true);
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    fetch(`${API_V2}/pricing/${encodeURIComponent(productId)}/resolve`, { headers })
      .then((res) => (res.ok ? res.json() : Promise.reject(new Error("Resolve"))))
      .then((data) => { if (!cancelled) setResolved(data); })
      .catch(() => { if (!cancelled) setResolved(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [productId, token]);
  return { resolved, loading };
}

export function useStockByProduct(productId) {
  const [stockList, setStockList] = useState([]);
  const [depotAvailable, setDepotAvailable] = useState(false);
  const [depotQuantity, setDepotQuantity] = useState(0);
  const [loading, setLoading] = useState(!!productId);
  const [error, setError] = useState(null);
  useEffect(() => {
    if (!productId) {
      setStockList([]);
      setDepotAvailable(false);
      setDepotQuantity(0);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    fetch(`${API_V2}/stock/product/${encodeURIComponent(productId)}`)
      .then((res) => (res.ok ? res.json() : Promise.reject(new Error("Stock"))))
      .then((data) => {
        if (cancelled) return;
        setStockList(Array.isArray(data?.stores) ? data.stores : Array.isArray(data) ? data : []);
        setDepotAvailable(!!data?.depot_available);
        setDepotQuantity(data?.depot_quantity || 0);
      })
      .catch((e) => { if (!cancelled) setError(e.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [productId]);
  return { stockList, depotAvailable, depotQuantity, loading, error };
}
